import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconPlus, IconSearch, IconX } from '@tabler/icons-react'
import { EntityParams } from 'app/models/entity-params'
import { FundGroup } from 'app/models/fund-group'
import {
  createFundGroup,
  deleteFundGroup,
  updateFundGroup,
} from 'app/state/ducks/fund-groups/thunks'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { FUND_GROUP_COPY } from './fund-group-copy'
import { FundGroupCard } from './fund-group-card'
import { FundGroupForm, FundGroupSubmitResult } from './fund-group-form'
import {
  FundGroupFormValues,
  entityNamesOf,
  filterGroups,
  indexEntities,
} from './fund-group-utils'

type Mode =
  { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; group: FundGroup }

type Props = {
  opened: boolean
  onClose: () => void
  fundManagerId: string
  groups: FundGroup[]
  entities: EntityParams[]
}

/**
 * "Manage Quick Allocations": list (search, badges, edit, inline delete) and, in place of the
 * list, the create / edit form. Every write goes through the fund-groups duck; the store is
 * the source of truth, so a successful save simply returns to the list.
 */
export function ManageFundGroupsModal({
  opened,
  onClose,
  fundManagerId,
  groups,
  entities,
}: Props) {
  const dispatch = useDispatch()
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [query, setQuery] = useState('')

  const entitiesById = useMemo(() => indexEntities(entities), [entities])
  const visible = useMemo(
    () => filterGroups(groups, query, entitiesById),
    [groups, query, entitiesById],
  )

  const close = () => {
    setMode({ kind: 'list' })
    setQuery('')
    onClose()
  }

  const toSubmitResult = (result: any): FundGroupSubmitResult => {
    if (result?.success) return { ok: true }
    // 409 = name collision → name field; anything else is the ledger refusing a member.
    return {
      ok: false,
      field: result?.status === 409 ? 'name' : 'entityIds',
      message: result?.payload,
    }
  }

  const handleSubmit = async (
    values: FundGroupFormValues,
  ): Promise<FundGroupSubmitResult> => {
    const result: any =
      mode.kind === 'edit'
        ? await dispatch(updateFundGroup(mode.group.id, values) as any)
        : await dispatch(createFundGroup({ fundManagerId, ...values }) as any)
    const mapped = toSubmitResult(result)
    if (mapped.ok) {
      SuccessNotification({
        title: FUND_GROUP_COPY.savedTitle,
        message: values.name,
      })
      setMode({ kind: 'list' })
    }
    return mapped
  }

  const handleDelete = async (group: FundGroup): Promise<boolean> => {
    const result: any = await dispatch(deleteFundGroup(group.id) as any)
    if (result?.success) {
      SuccessNotification({
        title: FUND_GROUP_COPY.deletedTitle,
        message: group.name,
      })
      return true
    }
    ErrorNotification({
      title: FUND_GROUP_COPY.deleteFailedTitle,
      message: result?.payload,
    })
    return false
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size={1180}
      withCloseButton={false}
      withinPortal
      padding={0}
      classNames={{ content: 'fund-group-modal' }}
    >
      <div className="fund-group-modal-header">
        <Text className="fund-group-modal-title">
          {mode.kind === 'edit'
            ? FUND_GROUP_COPY.editTitle
            : FUND_GROUP_COPY.manage}
        </Text>
        <ActionIcon aria-label="Close" variant="transparent" onClick={close}>
          <IconX size={28} color="#5b5b5b" />
        </ActionIcon>
      </div>

      <div className="fund-group-modal-body">
        {mode.kind === 'list' ? (
          <Stack spacing={24}>
            <Group position="apart" noWrap className="fund-group-modal-toolbar">
              <Text className="fund-group-modal-count">
                {FUND_GROUP_COPY.count(groups.length)}
              </Text>
              <TextInput
                className="fund-group-modal-search"
                size="md"
                icon={<IconSearch size={18} />}
                placeholder={FUND_GROUP_COPY.searchGroups}
                value={query}
                onChange={e => setQuery(e.currentTarget.value)}
                data-testid="fund-group-search"
              />
              <Button
                className="fund-group-primary-button fund-group-create-button"
                leftIcon={<IconPlus size={16} />}
                onClick={() => setMode({ kind: 'create' })}
              >
                {FUND_GROUP_COPY.createNew}
              </Button>
            </Group>

            {groups.length === 0 && (
              <div className="fund-group-empty">
                <Text className="fund-group-empty-title">
                  {FUND_GROUP_COPY.noGroupsYet}
                </Text>
                <Text className="fund-group-form-help">
                  {FUND_GROUP_COPY.noGroupsHint}
                </Text>
              </div>
            )}
            {groups.length > 0 && visible.length === 0 && (
              <div className="fund-group-empty">
                <Text className="fund-group-empty-title">
                  {FUND_GROUP_COPY.noResults(query)}
                </Text>
              </div>
            )}
            {visible.map(group => (
              <FundGroupCard
                key={group.id}
                group={group}
                entityNames={entityNamesOf(group, entitiesById)}
                onEdit={() => setMode({ kind: 'edit', group })}
                onDelete={() => handleDelete(group)}
              />
            ))}
          </Stack>
        ) : (
          <FundGroupForm
            key={mode.kind === 'edit' ? mode.group.id : 'create'}
            initial={mode.kind === 'edit' ? mode.group : undefined}
            entities={entities}
            existingGroups={groups}
            onSubmit={handleSubmit}
            onCancel={() => setMode({ kind: 'list' })}
          />
        )}
      </div>
    </Modal>
  )
}
